import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listScenarios, starScenario, unstarScenario, bookmarkScenario, unbookmarkScenario, unpublishScenario, deleteScenario } from '../api';
import { DEFAULT_SCENARIO_LIST_SORT } from '../api';
import type { ScenarioListSort } from '../api';
import { usePublishConfirm } from '../usePublishConfirm';
import { useUpdateScenario } from '../hooks';
import type { ScenarioListItem } from '../types';
import { SceneThumbnails } from './SceneThumbnails';
import { StoryboardThumbPreview } from './StoryboardThumbPreview';
import { FilterSection } from '../../../components/common/FilterSection';
import { SingleSelectDropdown } from '../../../components/common/SingleSelectDropdown';
import { apiClient } from '../../../services/apiClient';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { ArrowUpDown } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import { brandConfig } from '../../../config/brands';
import { useMaterialWorkflowStages } from '../../materials/hooks';
import { ORPHAN_WORKFLOW_STAGE_HINT, resolveWorkflowStageLabel } from '../../materials/workflowStageLabel';
import { isMotionEngineScenario } from '../../../components/generation/scenario/motionScenario';
import { deriveProductionMode } from '../productionMode';
import './ScenarioBookModal.css';
import '../../../styles/badges.css';

interface ScenarioBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectScenario: (scenarioId: string) => void;
  onSelectScenarios?: (scenarioIds: string[]) => void;
  currentUserId?: string; // 現在のユーザーID（作成者判定用）
  loadedScenarioIds?: string[]; // すでに読み込まれているシナリオIDのリスト
}

const ITEMS_PER_PAGE = 10;
// 制作進捗フィルタの「未設定」を表す擬似値 (ステージ ID と衝突しない)
const WORKFLOW_STAGE_NONE = '__none__';

/** 制作進捗バッジの塗り。ステージ色をそのまま背景にし、文字は黒で抜く (枠線なし)。
 *  色が無い (未設定 / 無効化済み) ときは CSS 側の既定値に任せる */
function workflowStageFillStyle(colorHex: string | null | undefined): React.CSSProperties | undefined {
  if (!colorHex || !/^#[0-9a-f]{6}$/i.test(colorHex.trim())) return undefined;
  return { '--workflow-stage-color': colorHex.trim() } as React.CSSProperties;
}

/** 制作進捗バッジの見た目のバリアント。
 *  - none: 未設定。目立たせない (塗りなし・枠線のみの反転表示)
 *  - done: 完了 (システム code 'done')。目立たせない (塗りも枠線もなしのグレー文字。ステージ色は使わない)
 *  - stage: それ以外。ステージ色で塗って黒文字
 *  計画 §3.4.2 (2026-10-26 の要望) */
function workflowStageVariant(resolved: ReturnType<typeof resolveWorkflowStageLabel>): 'none' | 'done' | 'stage' {
  if (resolved.kind === 'none') return 'none';
  if (resolved.kind === 'stage' && resolved.stage.code === 'done') return 'done';
  return 'stage';
}

type FilterType = 'all' | 'starred' | 'bookmarked' | 'published' | 'own';
type PublishFilter = '' | 'published' | 'unpublished';
type OwnerFilter = '' | 'own' | 'others';
// frame_mode の3バリアント (first_*) は「フレーム」としてまとめて扱う
// 制作方式フィルタ (docs/features/scene-location-and-production-mode-plan.md §3.7)。
// 「絵コンテから制作 / シーンごとに制作」の2値。旧値 'frame'/'reference' は API 側
// (list_scenarios の mode_filter) で 'per_scene' へ読み替えるため、この state 自体は
// 2値のみを持つ。
type ModeFilter = '' | 'storyboard' | 'per_scene';
// アスペクト比フィルタの選択肢 (シナリオで使われる比率。'' = すべて)
const ASPECT_RATIO_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'すべて' },
  { value: '16:9', label: '16:9（横）' },
  { value: '9:16', label: '9:16（縦）' },
  { value: '1:1', label: '1:1（正方形）' },
  { value: '4:3', label: '4:3' },
];
// 並び順の選択肢 (作成日 / 最終更新日 × 新しい順 / 古い順)。既定は作成日の新しい順。
// 選択は保存しない (モーダルを開くたびに既定へ戻る)
const SORT_OPTIONS: Array<{ value: ScenarioListSort; label: string }> = [
  { value: 'created_desc', label: '作成日（新しい順）' },
  { value: 'created_asc', label: '作成日（古い順）' },
  { value: 'updated_desc', label: '最終更新日（新しい順）' },
  { value: 'updated_asc', label: '最終更新日（古い順）' },
];

export function ScenarioBookModal({ isOpen, onClose, onSelectScenario, onSelectScenarios, currentUserId, loadedScenarioIds = [] }: ScenarioBookModalProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState<ScenarioListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [publishFilter, setPublishFilter] = useState<PublishFilter>('');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('');
  const [hasImagesFilter, setHasImagesFilter] = useState(false);
  const [hasVideosFilter, setHasVideosFilter] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [productFilter, setProductFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  // 公開確認モーダル (採用素材の共有移行を選ぶ) は共通フックに集約
  const { requestPublish, dialog: publishDialog } = usePublishConfirm({
    onPublished: (scenarioId) => {
      setScenarios(prev => prev.map(s =>
        s.id === scenarioId ? { ...s, published: true } : s
      ));
    },
  });
  const [modeFilter, setModeFilter] = useState<ModeFilter>('');
  const [aspectRatioFilter, setAspectRatioFilter] = useState('');
  // 制作進捗フィルタ ('' = すべて / WORKFLOW_STAGE_NONE = 未設定 / それ以外 = ステージ ID)。
  // 他フィルタと同じ単一選択。組織に有効ステージが無ければフィルタ・バッジごと出さない (ゲート)。
  // docs/features/workflow-stage-take8-and-scenario-book-plan.md §3.4.2
  const [workflowStageFilter, setWorkflowStageFilter] = useState('');
  const workflowStagesQuery = useMaterialWorkflowStages();
  const workflowStages = workflowStagesQuery.data ?? [];
  const workflowStageEnabled = workflowStages.length > 0;
  // カード上のバッジから進捗を直接変更する (所有者のみ。BE も所有者検証で拒否する)。
  // 保存中のカードは Set で管理し、完了したカードだけ解放する (A の保存中に B を変えても
  // A が再操作可能にならず、同じカードへの更新が重ならない)。
  // 成功したら現在のフィルタ・並び順で一覧と件数を取り直す (この一覧は listScenarios の
  // 結果をローカル state に持つため、useUpdateScenario のキャッシュ無効化だけでは更新されない。
  // 「確認中」で絞った一覧で「完了」に変えたカードはこれで一覧から消え、件数も減る)。
  const updateScenarioMutation = useUpdateScenario();
  const [workflowStageUpdatingIds, setWorkflowStageUpdatingIds] = useState<ReadonlySet<string>>(() => new Set());
  const handleWorkflowStageChange = async (scenarioId: string, value: string) => {
    if (workflowStageUpdatingIds.has(scenarioId)) return;
    const next = value || null;
    setWorkflowStageUpdatingIds(prev => new Set(prev).add(scenarioId));
    try {
      await updateScenarioMutation.mutateAsync({ scenarioId, payload: { workflow_stage_id: next } });
      // 再取得が終わるまでの間も最新値を見せる (再取得で上書きされる)
      setScenarios(prev => prev.map(s => (s.id === scenarioId ? { ...s, workflow_stage_id: next } : s)));
      // 保存中にフィルタ・ページ・並び順が変わっていても最新の表示条件で取り直す
      // (この関数が作られたレンダーの loadScenarios は保存開始時の条件を閉じ込めているため ref 経由で呼ぶ)
      await loadScenariosRef.current({ silent: true });
    } catch (err) {
      console.error('Failed to update workflow stage:', err);
      toast.error('制作進捗の更新に失敗しました');
    } finally {
      setWorkflowStageUpdatingIds(prev => {
        const nextSet = new Set(prev);
        nextSet.delete(scenarioId);
        return nextSet;
      });
    }
  };
  // 並び順はフィルタではないので「クリア」では戻さない
  const [sortKey, setSortKey] = useState<ScenarioListSort>(DEFAULT_SCENARIO_LIST_SORT);

  // 一括選択: チェックした順に ID を保持
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleSelection = useCallback((scenarioId: string) => {
    setSelectedIds(prev =>
      prev.includes(scenarioId)
        ? prev.filter(id => id !== scenarioId)
        : [...prev, scenarioId]
    );
  }, []);

  // モーダルを閉じたら選択をリセット
  useEffect(() => {
    if (!isOpen) setSelectedIds([]);
  }, [isOpen]);

  const isMobile = useIsMobile();

  // モバイル: スワイプダウンで閉じるジェスチャー
  const [sheetDragging, setSheetDragging] = useState(false);
  const [sheetDragOffset, setSheetDragOffset] = useState(0);
  const sheetSwipeStartRef = useRef(0);

  const handleSheetSwipeStart = useCallback((e: React.TouchEvent) => {
    sheetSwipeStartRef.current = e.touches[0].clientY;
    setSheetDragging(true);
  }, []);

  const handleSheetSwipeMove = useCallback((e: React.TouchEvent) => {
    if (!sheetDragging) return;
    const deltaY = e.touches[0].clientY - sheetSwipeStartRef.current;
    if (deltaY > 0) {
      setSheetDragOffset(deltaY);
    }
  }, [sheetDragging]);

  const handleSheetSwipeEnd = useCallback(() => {
    setSheetDragging(false);
    if (sheetDragOffset > 100) {
      onClose();
    }
    setSheetDragOffset(0);
  }, [sheetDragOffset, onClose]);

  useLockBodyScroll(isOpen && isMobile);

  // Amazon商品を取得
  const { data: amazonProductsData } = useQuery({
    queryKey: ['products', 'amazon', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/amazon/products', {
        params: { limit: 100 }
      });
      return response.data;
    },
  });

  // マニュアル商品を取得
  const { data: manualProductsData } = useQuery({
    queryKey: ['products', 'manual', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/products', {
        params: { limit: 100 }
      });
      return response.data;
    },
  });

  // ブランド一覧を取得
  const { data: brandsData, isLoading: isLoadingBrands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const response = await apiClient.get('/brands');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // お気に入りブランドを取得（ブランドフィルタの並び順に使用）
  const { data: favoriteBrandsData } = useQuery({
    queryKey: ['user', 'favorite-brands'],
    queryFn: async () => {
      const response = await apiClient.get('/user/favorite-brands');
      return response.data as { favorites: { brand_id: string | null; brand_name: string | null }[] };
    },
    staleTime: 60_000,
  });

  // お気に入りキーの Set（brand_id を優先、なければ brand_name）
  const favoriteBrandKeys = useMemo(() => {
    const set = new Set<string>();
    for (const fav of favoriteBrandsData?.favorites || []) {
      const key = fav.brand_id || fav.brand_name;
      if (key) set.add(key);
    }
    return set;
  }, [favoriteBrandsData]);

  const allBrandOptions = useMemo<Array<{ value: string; label: string; isFavorite: boolean }>>(() => {
    const brands = brandsData?.brands || [];
    // お気に入りを上位に固める（同グループ内は元の順序を維持＝stable sort）
    return brands
      .map((b: any, index: number) => ({
        value: b.id,
        label: b.display_name || b.brand_name,
        isFavorite: favoriteBrandKeys.has(b.id),
        index,
      }))
      .sort((a: any, b: any) => {
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        return a.index - b.index;
      })
      .map(({ value, label, isFavorite }: { value: string; label: string; isFavorite: boolean }) => ({ value, label, isFavorite }));
  }, [brandsData, favoriteBrandKeys]);

  // 商品リストを統合した選択肢として取得（brand_id付き）
  const allProductOptions = useMemo<Array<{ value: string; label: string; brand_id?: string }>>(() => {
    const options: Array<{ value: string; label: string; brand_id?: string }> = [];
    const amazonProducts = amazonProductsData?.products || [];
    for (const product of amazonProducts) {
      const title = product.title?.substring(0, 30) || '';
      const ellipsis = product.title?.length > 30 ? '...' : '';
      options.push({
        value: `asin:${product.asin}`,
        label: `${title}${ellipsis} (${product.asin})`,
        brand_id: product.brand_id || undefined,
      });
    }
    const manualProducts = manualProductsData?.products || [];
    for (const product of manualProducts) {
      const title = (product.title || product.sku || '').substring(0, 30);
      const ellipsis = (product.title || product.sku || '').length > 30 ? '...' : '';
      options.push({
        value: `product:${product.id}`,
        label: `${title}${ellipsis}`,
        brand_id: product.brand_id || undefined,
      });
    }
    return options;
  }, [amazonProductsData, manualProductsData]);

  // ブランド↔商品の相互絞り込み
  const productOptions = useMemo(() => {
    if (!brandFilter) return allProductOptions;
    return allProductOptions.filter(opt => opt.brand_id === brandFilter);
  }, [allProductOptions, brandFilter]);

  const brandOptions = useMemo(() => {
    if (!productFilter) return allBrandOptions;
    const selectedProduct = allProductOptions.find(opt => opt.value === productFilter);
    if (!selectedProduct?.brand_id) return allBrandOptions;
    return allBrandOptions.filter(b => b.value === selectedProduct.brand_id);
  }, [allBrandOptions, productFilter, allProductOptions]);

  // Load scenarios when modal opens or when page/filter/search changes
  useEffect(() => {
    if (isOpen) {
      loadScenarios();
    }
  }, [isOpen, currentPage, activeFilter, searchQuery, productFilter, brandFilter, publishFilter, ownerFilter, hasImagesFilter, hasVideosFilter, modeFilter, aspectRatioFilter, workflowStageFilter, sortKey]);

  // silent: 進捗変更後の取り直しなど、一覧を消さずに裏で更新したいときに true
  // (読み込み表示に切り替えるとカードが一瞬消えてスクロール位置も飛ぶため)
  // 一覧取得のリクエスト世代。フィルタ・ページ・並び順を変えるたびに新しい取得が走るので、
  // 古い取得の応答 (保存中にフィルタを切り替えた場合など) が最新の一覧・件数・ページを
  // 上書きしないよう、応答時に世代が進んでいたら捨てる
  const loadGenerationRef = useRef(0);
  const loadScenarios = async (opts: { silent?: boolean } = {}) => {
    const generation = ++loadGenerationRef.current;
    const isLatest = () => generation === loadGenerationRef.current;
    if (!opts.silent) setLoading(true);
    setError(null);
    try {
      const asinValue = productFilter.startsWith('asin:') ? productFilter.slice(5) : undefined;
      const productIdValue = productFilter.startsWith('product:') ? productFilter.slice(8) : undefined;
      const data = await listScenarios({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        filter: activeFilter,
        search: searchQuery || undefined,
        asin: asinValue,
        product_id: productIdValue,
        brand_id: brandFilter || undefined,
        publish_filter: publishFilter || undefined,
        owner_filter: ownerFilter || undefined,
        has_images: hasImagesFilter || undefined,
        has_videos: hasVideosFilter || undefined,
        mode_filter: modeFilter || undefined,
        aspect_ratio: aspectRatioFilter || undefined,
        sort: sortKey,
        workflow_stage_ids: workflowStageFilter && workflowStageFilter !== WORKFLOW_STAGE_NONE ? [workflowStageFilter] : undefined,
        no_workflow_stage: workflowStageFilter === WORKFLOW_STAGE_NONE || undefined,
      });
      if (!isLatest()) return;
      setScenarios(data.items);
      setTotalCount(data.total);
      // 進捗変更などで最終ページの最後のカードが対象外になり、現在ページが空になったら
      // 最後に残ったページへ戻す (currentPage の変更で再取得される)
      if (data.items.length === 0 && data.total > 0 && currentPage > 1) {
        setCurrentPage(Math.max(1, Math.ceil(data.total / ITEMS_PER_PAGE)));
      }
    } catch (err) {
      if (!isLatest()) return;
      setError('シナリオの読み込みに失敗しました');
      console.error('Failed to load scenarios:', err);
    } finally {
      if (isLatest()) setLoading(false);
    }
  };
  // 常に最新レンダーの loadScenarios (= 最新の表示条件) を指す
  const loadScenariosRef = useRef(loadScenarios);
  loadScenariosRef.current = loadScenarios;

  // お気に入りのトグル（作成者のみ）
  const handleToggleStar = async (scenarioId: string, currentStarred: boolean) => {
    try {
      if (currentStarred) {
        await unstarScenario(scenarioId);
      } else {
        await starScenario(scenarioId);
      }

      // 楽観的UI更新
      setScenarios(prev => prev.map(s =>
        s.id === scenarioId ? { ...s, starred: !currentStarred } : s
      ));
    } catch (err) {
      console.error('Failed to toggle star:', err);
      // エラー時は元に戻す
      setScenarios(prev => prev.map(s =>
        s.id === scenarioId ? { ...s, starred: currentStarred } : s
      ));
    }
  };

  // しおりのトグル（全員）
  const handleToggleBookmark = async (scenarioId: string, currentBookmarked: boolean, currentCount: number) => {
    try {
      if (currentBookmarked) {
        await unbookmarkScenario(scenarioId);
      } else {
        await bookmarkScenario(scenarioId);
      }

      // 楽観的UI更新
      setScenarios(prev => prev.map(s =>
        s.id === scenarioId ? {
          ...s,
          is_bookmarked: !currentBookmarked,
          bookmark_count: currentBookmarked ? (currentCount - 1) : (currentCount + 1)
        } : s
      ));
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
      // エラー時は元に戻す
      setScenarios(prev => prev.map(s =>
        s.id === scenarioId ? {
          ...s,
          is_bookmarked: currentBookmarked,
          bookmark_count: currentCount
        } : s
      ));
    }
  };

  // パブリッシュのトグル（作成者のみ）
  const handleTogglePublish = async (scenarioId: string, currentPublished: boolean) => {
    // 非公開化はそのまま即実行 (確認不要)
    if (currentPublished) {
      try {
        await unpublishScenario(scenarioId);
        setScenarios(prev => prev.map(s =>
          s.id === scenarioId ? { ...s, published: false } : s
        ));
      } catch (err) {
        console.error('Failed to unpublish:', err);
      }
      return;
    }
    // 公開は共通フックの確認モーダルを開き、採用素材の共有移行を選ばせる
    void requestPublish(scenarioId);
  };

  // シナリオ削除（作成者のみ）
  const handleDeleteScenario = async (scenarioId: string, _scenarioTitle: string) => {
    try {
      await deleteScenario(scenarioId);
      toast.success('シナリオをごみ箱に移動しました');

      // リストから削除
      setScenarios(prev => prev.filter(s => s.id !== scenarioId));
      setTotalCount(prev => prev - 1);
    } catch (err) {
      console.error('Failed to delete scenario:', err);
      toast.error('シナリオの削除に失敗しました');
    }
  };

  // ページネーション計算（サーバーサイドで処理済み）
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalCount);

  // 検索クエリまたはフィルターが変更されたらページを1に戻す
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter, productFilter, brandFilter, publishFilter, ownerFilter, hasImagesFilter, hasVideosFilter, modeFilter, aspectRatioFilter, workflowStageFilter, sortKey]);

  // 検索入力のデバウンス処理
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(true); // デフォルトで展開

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInputValue);
    }, 300); // 300msデバウンス

    return () => clearTimeout(timer);
  }, [searchInputValue]);

  // フィルタがアクティブかどうかを判定
  const hasActiveFilters = activeFilter !== 'all' || publishFilter !== '' || ownerFilter !== '' || productFilter !== '' || brandFilter !== '' || searchQuery !== '' || hasImagesFilter || hasVideosFilter || modeFilter !== '' || aspectRatioFilter !== '' || workflowStageFilter !== '';

  // フィルタをクリアする関数
  const clearFilters = () => {
    setActiveFilter('all');
    setPublishFilter('');
    setOwnerFilter('');
    setProductFilter('');
    setBrandFilter('');
    setSearchInputValue('');
    setSearchQuery('');
    setHasImagesFilter(false);
    setHasVideosFilter(false);
    setModeFilter('');
    setAspectRatioFilter('');
    setWorkflowStageFilter('');
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="scenario-book-overlay" onClick={onClose}>
      <div
        className={`scenario-book-modal ${sheetDragging ? 'sheet-dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={isMobile && sheetDragOffset > 0 ? { transform: `translateY(${sheetDragOffset}px)` } : undefined}
      >
        {isMobile && (
          <div
            className="scenario-book-drag-handle"
            onTouchStart={handleSheetSwipeStart}
            onTouchMove={handleSheetSwipeMove}
            onTouchEnd={handleSheetSwipeEnd}
          />
        )}
        <div
          className="scenario-book-header"
          onTouchStart={isMobile ? handleSheetSwipeStart : undefined}
          onTouchMove={isMobile ? handleSheetSwipeMove : undefined}
          onTouchEnd={isMobile ? handleSheetSwipeEnd : undefined}
        >
          <h2>
            <img src="/book_book_pages.svg" alt="" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
            シナリオブック
          </h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="scenario-book-filters">
          <FilterSection
            expanded={filterExpanded}
            onToggleExpanded={() => setFilterExpanded(!filterExpanded)}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            clearLabel="クリア"
            headerRight={<span className="result-count-header">（{totalCount}件）</span>}
          >
            <div className="filter-row">
              <button
                className={`filter-pill ${activeFilter === 'starred' ? 'active' : ''}`}
                onClick={() => setActiveFilter(activeFilter === 'starred' ? 'all' : 'starred')}
              >
                <img src="/star_1.svg" alt="" style={{ width: '11px', height: '11px', marginRight: '4px', verticalAlign: 'middle', opacity: 0.4 }} />
                お気に入り
              </button>
              <button
                className={`filter-pill ${activeFilter === 'bookmarked' ? 'active' : ''}`}
                onClick={() => setActiveFilter(activeFilter === 'bookmarked' ? 'all' : 'bookmarked')}
              >
                <img src="/book_close_bookmark_1.svg" alt="" style={{ width: '12px', height: '12px', marginRight: '4px', verticalAlign: 'middle' }} />
                しおり
              </button>
              <button
                className={`filter-pill ${hasImagesFilter ? 'active' : ''}`}
                onClick={() => setHasImagesFilter(!hasImagesFilter)}
              >
                <img src="/picture_stack_landscape.svg" alt="" style={{ width: '12px', height: '12px', marginRight: '4px', verticalAlign: 'middle' }} />
                画像あり
              </button>
              <button
                className={`filter-pill ${hasVideosFilter ? 'active' : ''}`}
                onClick={() => setHasVideosFilter(!hasVideosFilter)}
              >
                <img src="/video_player_movie.svg" alt="" style={{ width: '12px', height: '12px', marginRight: '4px', verticalAlign: 'middle' }} />
                動画あり
              </button>
            </div>
            <div className="filter-row">
              {/* 並び順 (2026-10-26 利用者決定): ブランド, 案件, アスペクト比, 制作方式, 制作進捗, 作成者, 共有状態 */}
              <SingleSelectDropdown
                label={isLoadingBrands ? 'ブランド (読込中...)' : 'ブランド'}
                options={[
                  { value: '', label: 'すべて' },
                  ...brandOptions.map(({ value, label, isFavorite }) => ({
                    value,
                    label,
                    ...(isFavorite ? { icon: '/star_1_gold.svg' } : {}),
                  })),
                ]}
                value={brandFilter}
                onChange={setBrandFilter}
                fitContent
              />
              <SingleSelectDropdown
                label={brandConfig.labels.productShort}
                labelOnly
                className={`product-filter-dropdown${!productFilter ? ' product-filter-default' : ''}`}
                dropdownClassName="filter-dropdown-menu"
                options={[
                  { value: '', label: 'すべて' },
                  ...productOptions,
                ]}
                value={productFilter}
                onChange={setProductFilter}
                fitContent
              />
              <SingleSelectDropdown
                label="アスペクト比"
                options={ASPECT_RATIO_FILTER_OPTIONS}
                value={aspectRatioFilter}
                onChange={setAspectRatioFilter}
              />
              <SingleSelectDropdown
                label="制作方式"
                options={[
                  { value: '', label: 'すべて' },
                  { value: 'storyboard', label: '絵コンテ' },
                  { value: 'per_scene', label: 'シーンごと' },
                ]}
                value={modeFilter}
                onChange={(v) => setModeFilter(v as ModeFilter)}
              />
              {workflowStageEnabled && (
                <SingleSelectDropdown
                  label="制作進捗"
                  options={[
                    { value: '', label: 'すべて' },
                    { value: WORKFLOW_STAGE_NONE, label: '未設定' },
                    ...workflowStages.map((stage) => ({ value: stage.id, label: stage.label })),
                  ]}
                  value={workflowStageFilter}
                  onChange={setWorkflowStageFilter}
                />
              )}
              {currentUserId && (
                <SingleSelectDropdown
                  label="作成者"
                  options={[
                    { value: '', label: 'すべて' },
                    { value: 'own', label: '自分が作成' },
                    { value: 'others', label: '他のユーザーが作成' },
                  ]}
                  value={ownerFilter}
                  onChange={(v) => setOwnerFilter(v as OwnerFilter)}
                />
              )}
              <SingleSelectDropdown
                label="共有状態"
                options={[
                  { value: '', label: 'すべて' },
                  { value: 'published', label: '共有済み' },
                  { value: 'unpublished', label: '未共有' },
                ]}
                value={publishFilter}
                onChange={(v) => setPublishFilter(v as PublishFilter)}
              />
              {searchExpanded || searchInputValue ? (
                <input
                  type="search"
                  placeholder="シナリオを検索..."
                  value={searchInputValue}
                  onChange={(e) => setSearchInputValue(e.target.value)}
                  className="search-box"
                  autoFocus
                  onBlur={() => { if (!searchInputValue) setSearchExpanded(false); }}
                />
              ) : (
                <button
                  type="button"
                  className="search-icon-button"
                  onClick={() => setSearchExpanded(true)}
                  aria-label="検索"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </button>
              )}
            </div>
          </FilterSection>
        </div>

        {/* 並び順: フィルタ領域とカードの間・右寄せ。塗りなし・枠線なしのドロップダウンで
            作成日順 / 最終更新日順 を選ぶ */}
        <div className="scenario-book-sort-row">
          <SingleSelectDropdown
            label="並び順"
            labelOnly
            alwaysShowSelected
            fitContent
            unstyledTrigger
            triggerClassName="scenario-book-sort-trigger"
            triggerIcon={<ArrowUpDown size={13} />}
            defaultValue={DEFAULT_SCENARIO_LIST_SORT}
            options={SORT_OPTIONS}
            value={sortKey}
            onChange={(v) => setSortKey(v as ScenarioListSort)}
          />
        </div>

        <div className="scenario-book-content">
          {loading && (
            <div className="loading-state">
              <p>読み込み中...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={() => { void loadScenarios(); }}>再試行</button>
            </div>
          )}

          {!loading && !error && scenarios.length === 0 && (
            <div className="empty-state">
              <p>シナリオが見つかりませんでした</p>
            </div>
          )}

          {!loading && !error && scenarios.length > 0 && (
            <>
              <div className="scenario-list">
                {scenarios.map((scenario) => {
                  const isStoryboard = scenario.frame_mode === 'storyboard';
                  const storyboardThumb = isStoryboard
                    ? scenario.reference_image_urls?.[0]?.thumbnailUrl ||
                      scenario.reference_image_urls?.[0]?.sourceUrl
                    : null;
                  const storyboardHighRes = isStoryboard
                    ? scenario.reference_image_urls?.[0]?.sourceUrl
                    : null;
                  // 絵コンテモードでも動画か最初のフレーム画像が生成済みなら、絵コンテではなく
                  // 生成物のサムネイルを出す (動画 > フレーム画像の順で、シーン順の先頭を採用)。
                  // ホバーの高解像度ポップアップは絵コンテのまま残す。
                  const sceneMediaThumb = isStoryboard
                    ? (() => {
                        const scenes = scenario.scenes ?? [];
                        const withVideo = scenes.find((s) => s.video_thumbnail_url);
                        if (withVideo?.video_thumbnail_url) return withVideo.video_thumbnail_url;
                        const withImage = scenes.find((s) => s.thumbnail_url || s.image_url);
                        return withImage?.thumbnail_url || withImage?.image_url || null;
                      })()
                    : null;
                  // 絵コンテモードのレイアウト分岐:
                  // - 9:16 は常に縦長の 2 列レイアウト (生成物があれば絵コンテをそれに差し替える)
                  // - 16:9 / 1:1 で絵コンテのみ (生成物なし) → 縦長の絵コンテを見せる 2 列レイアウト
                  // - 16:9 / 1:1 で生成物あり、または絵コンテも未生成 → 「シーンごとに制作」と同じ
                  //   レイアウトで、サムネイル 1 枚 (生成物 or 未生成プレースホルダー) だけを置く
                  const useStoryboardLayout = isStoryboard && (
                    scenario.aspect_ratio === '9:16' || (!sceneMediaThumb && !!storyboardThumb)
                  );

                  const headerBlock = (
                    <div className="scenario-card-header">
                      <label className="scenario-checkbox" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(scenario.id)}
                          onChange={() => toggleSelection(scenario.id)}
                        />
                      </label>
                      <div className="scenario-title">
                        <h3
                          className="scenario-title-toggle"
                          onClick={() => toggleSelection(scenario.id)}
                          title={selectedIds.includes(scenario.id) ? '選択を外す' : '選択する'}
                        >
                          {scenario.title}
                        </h3>
                        {/* 作成者の場合のみお気に入りボタン表示 */}
                        {currentUserId && scenario.user_id === currentUserId && (
                          <button
                            className="star-button-icon"
                            onClick={() => handleToggleStar(scenario.id, scenario.starred)}
                            title={scenario.starred ? 'お気に入りから外す' : 'お気に入りに追加'}
                          >
                            <img src={scenario.starred ? "/star_1_gold.svg" : "/star_1.svg"} alt="お気に入り" style={{ width: '14px', height: '14px', opacity: scenario.starred ? 1 : 0.4 }} />
                          </button>
                        )}
                        {/* 制作進捗バッジ。ラベル群の先頭 (お気に入りの右・台本取込の左) に置く。組織で有効なら
                            未設定でも出す。枠線なしでバッジ全体をステージ色で塗り、文字は黒で抜く (未設定は灰色)。
                            所有者にはカスタムドロップダウン (SingleSelectDropdown・シェブロン付き) で
                            その場で値を付けたり変えたりできる。他ユーザーには表示のみ */}
                        {workflowStageEnabled && (() => {
                          const resolved = resolveWorkflowStageLabel(scenario.workflow_stage_id, workflowStages);
                          const variant = workflowStageVariant(resolved);
                          const fillStyle = variant === 'stage' && resolved.kind === 'stage'
                            ? workflowStageFillStyle(resolved.colorHex)
                            : undefined;
                          const variantClass = `scenario-workflow-stage--${variant}`;
                          const hint = resolved.kind === 'orphan' ? ORPHAN_WORKFLOW_STAGE_HINT : undefined;
                          const canEdit = !!currentUserId && scenario.user_id === currentUserId;
                          if (!canEdit) {
                            return (
                              <span
                                className={`badge scenarioTag scenario-workflow-stage ${variantClass}`}
                                data-testid="scenario-workflow-stage-badge"
                                title={hint}
                                style={fillStyle}
                              >
                                {resolved.label}
                              </span>
                            );
                          }
                          const options = [
                            { value: '', label: '未設定' },
                            ...workflowStages.map((stage) => ({ value: stage.id, label: stage.label })),
                            ...(resolved.kind === 'orphan' && scenario.workflow_stage_id
                              ? [{ value: scenario.workflow_stage_id, label: '（無効）現在の値' }]
                              : []),
                          ];
                          return (
                            <span
                              className={`scenario-workflow-stage-dd ${variantClass}`}
                              data-testid="scenario-workflow-stage-select"
                              title={hint ?? '制作進捗を変更'}
                              style={fillStyle}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SingleSelectDropdown
                                label="制作進捗"
                                options={options}
                                value={scenario.workflow_stage_id ?? ''}
                                onChange={(v) => { void handleWorkflowStageChange(scenario.id, v); }}
                                labelOnly
                                alwaysShowSelected
                                fitContent
                                unstyledTrigger
                                triggerClassName="badge scenarioTag scenario-workflow-stage scenario-workflow-stage-trigger"
                                disabled={workflowStageUpdatingIds.has(scenario.id)}
                              />
                            </span>
                          );
                        })()}
                        {/* 台本から取り込んだシナリオ (generation_metadata.origin='imported'。§4.3) */}
                        {scenario.generation_metadata?.origin === 'imported' && (
                          <span
                            className="badge badge-gray scenarioTag scenario-format-badge"
                            data-testid="scenario-imported-badge"
                            title={scenario.generation_metadata.imported_script?.filename
                              ? `台本: ${scenario.generation_metadata.imported_script.filename}`
                              : undefined}
                          >
                            台本取込
                          </span>
                        )}
                        {/* 特殊なシナリオ形式のバッジ (台本取込と同じ位置・同じ見た目)。
                            全編モーショングラフィックス (generation_metadata.production_engine='motion') と
                            歌モノ (generation_metadata.bgm_mode='lyrics')。両立しない (モーションでは歌モノを選べない) */}
                        {isMotionEngineScenario(scenario) && (
                          <span
                            className="badge badge-gray scenarioTag scenario-format-badge"
                            data-testid="scenario-motion-badge"
                            title="全編モーショングラフィックスで制作したシナリオ"
                          >
                            モーション
                          </span>
                        )}
                        {scenario.generation_metadata?.bgm_mode === 'lyrics' && (
                          <span
                            className="badge badge-gray scenarioTag scenario-format-badge"
                            data-testid="scenario-lyrics-badge"
                            title="歌モノ (歌詞付き BGM) のシナリオ"
                          >
                            歌モノ
                          </span>
                        )}
                        {/* 制作方式 (絵コンテから制作 / シーンごとに制作) を「絵コンテ」「シーン」と短く出す。旧シナリオタグと同じ見た目。
                            正本は generation_metadata.production_mode で、旧データは frame_mode から導出する。
                            シナリオの訴求軸タグ (tags) はシナリオブックでは出さない (2026-10-26 利用者決定) */}
                        <span className="badge badge-gray scenarioTag" data-testid="scenario-production-mode-badge">
                          {deriveProductionMode(scenario) === 'storyboard' ? '絵コンテ' : 'シーン'}
                        </span>
                      </div>
                      <div className="scenario-meta">
                      </div>
                      <div className="scenario-header-actions">
                        {/* しおりボタンは全ユーザーが使える */}
                        {currentUserId && (
                          <button
                            className={`header-action-button bookmark-button ${scenario.is_bookmarked ? 'active' : ''}`}
                            onClick={() => handleToggleBookmark(
                              scenario.id,
                              scenario.is_bookmarked || false,
                              scenario.bookmark_count || 0
                            )}
                            title={scenario.is_bookmarked ? 'しおりを外す' : 'しおりを挟む'}
                          >
                            <img src="/book_close_bookmark_1.svg" alt="しおり" style={{ width: '16px', height: '16px', opacity: scenario.is_bookmarked ? 1 : 0.4 }} />
                          </button>
                        )}
                        {/* 作成者の場合のみ公開・削除ボタン表示 */}
                        {currentUserId && scenario.user_id === currentUserId && (
                          <>
                            <button
                              className={`header-action-button publish-button ${scenario.published ? 'active' : ''}`}
                              onClick={() => handleTogglePublish(scenario.id, scenario.published)}
                              title={scenario.published ? '共有を解除する' : '共有する'}
                            >
                              <img src="/common_file_give_hand_3.svg" alt={scenario.published ? "共有中" : "未共有"} style={{ width: '16px', height: '16px', opacity: scenario.published ? 1 : 0.4 }} />
                            </button>
                            <button
                              className="header-action-button delete-button"
                              onClick={() => handleDeleteScenario(scenario.id, scenario.title)}
                              title="シナリオを削除"
                            >
                              <img src="/bin_1.svg" alt="削除" style={{ width: '16px', height: '16px' }} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );

                  const infoBlock = (
                    <div className="scenario-info">
                      <span className="info-item" title={scenario.user_id === currentUserId ? '自分が作成したシナリオ' : '他のユーザーが作成したシナリオ'}>
                        <img
                          src={scenario.user_id === currentUserId ? '/single_neutral_actions_edit_1.svg' : '/multiple_neutral_2.svg'}
                          alt={scenario.user_id === currentUserId ? '自分が作成' : '他のユーザーが作成'}
                          style={{ width: '14px', height: '14px', opacity: 0.6, verticalAlign: 'middle' }}
                        />
                      </span>
                      <span
                        className="info-item"
                        title={sortKey.startsWith('updated') ? '最終更新日時' : '作成日時'}
                      >
                        {new Date(sortKey.startsWith('updated') ? (scenario.updated_at || scenario.created_at) : scenario.created_at).toLocaleString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {scenario.source_type === 'asin' && scenario.asin && (
                        <span className="info-item info-item-asin">
                          {(() => {
                            const product = amazonProductsData?.products?.find((p: any) => p.asin === scenario.asin);
                            const productName = product?.title || '';
                            return productName ? `${productName} (${scenario.asin})` : scenario.asin;
                          })()}
                        </span>
                      )}
                      {scenario.source_type === 'manual' && !!scenario.custom_input?.product_id && (
                        <span className="info-item info-item-asin">
                          {(() => {
                            const pid = scenario.custom_input.product_id as string;
                            const product = manualProductsData?.products?.find((p: any) => p.id === pid);
                            return product?.title || product?.sku || (scenario.custom_input.product_name as string) || pid;
                          })()}
                        </span>
                      )}
                    </div>
                  );

                  const summaryBlock = (
                    <p className="scenario-summary">{scenario.summary || ''}</p>
                  );

                  // 動画素材を持つシーンが1つでもあれば、読み込みを挟まず動画エディタへ直行できる
                  // アイコンボタンを「シナリオを開く」の左に出す (ラベル無し・カスタムツールチップのみ)。
                  const hasVideos = !!scenario.scenes?.some((s) => s.video_material_id);

                  const loadButton = (
                    <div className="scenario-load-button">
                      {hasVideos && (
                        <button
                          type="button"
                          className="btn-modal-secondary scenario-editor-button"
                          data-tooltip="動画エディタで開く"
                          aria-label="動画エディタで開く"
                          onClick={() => {
                            onClose();
                            navigate(`/studio/video/${scenario.id}/editor`);
                          }}
                        >
                          <img src="/video_player_slider.svg" alt="" aria-hidden="true" />
                        </button>
                      )}
                      <button
                        className="btn-modal"
                        onClick={() => {
                          onSelectScenario(scenario.id);
                          onClose();
                        }}
                      >
                        {loadedScenarioIds.includes(scenario.id) ? '開き直す' : 'シナリオを開く'}
                      </button>
                    </div>
                  );

                  const bodyContent = useStoryboardLayout ? (
                    // 2列化前と同じ縦幅になるよう、説明と「シナリオを開く」ボタンを縦積みにする
                    <div className="scenario-card-content scenario-card-content-storyboard">
                      {infoBlock}
                      {summaryBlock}
                      {loadButton}
                    </div>
                  ) : (
                    <div className="scenario-card-content">
                      {infoBlock}

                      {isStoryboard ? (
                        // 絵コンテモード (16:9 / 1:1) はシーン数ぶんのプレースホルダーや番号は
                        // 出さず、サムネイルを 1 枚だけ置く (生成物があればそれ + ホバーで絵コンテを
                        // 高解像度表示。絵コンテも生成物も無ければ未生成プレースホルダー)
                        <div className="scene-thumbnails">
                          <div className={`scene-thumbnail ${scenario.aspect_ratio === '1:1' ? 'square' : ''}`}>
                            {sceneMediaThumb ? (
                              <StoryboardThumbPreview
                                thumbnailUrl={sceneMediaThumb}
                                highResUrl={storyboardHighRes || undefined}
                                alt="生成済みシーン"
                              />
                            ) : (
                              <div className="scene-thumbnail-placeholder">
                                <span className="scene-thumbnail-empty-label">ストーリーボード<br />未生成</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : scenario.scenes && scenario.scenes.length > 0 && (
                        <SceneThumbnails
                          scenes={scenario.scenes}
                          maxThumbnails={5}
                          aspectRatio={scenario.aspect_ratio}
                        />
                      )}

                      <div className="scenario-bottom-row">
                        {summaryBlock}
                        {loadButton}
                      </div>
                    </div>
                  );

                  // シナリオブック (ストーリーボード) は縦長なので、カード自体を2列に分割し、
                  // 左列をサムネイル、右列をヘッダー・説明・「シナリオを開く」ボタンに割り当てる
                  if (useStoryboardLayout) {
                    return (
                      <div
                        key={scenario.id}
                        className={`scenario-card scenario-card-storyboard ${selectedIds.includes(scenario.id) ? 'scenario-card-selected' : ''}`}
                      >
                        {/* 生成物 (9:16) も絵コンテと同じく列の上下いっぱいに広げ、左右を切り出す */}
                        <div className="scenario-card-thumb-col">
                          <div className="scene-thumbnail">
                            {sceneMediaThumb ? (
                              <StoryboardThumbPreview
                                thumbnailUrl={sceneMediaThumb}
                                highResUrl={storyboardHighRes || undefined}
                                alt="生成済みシーン"
                              />
                            ) : storyboardThumb ? (
                              <StoryboardThumbPreview
                                thumbnailUrl={storyboardThumb}
                                highResUrl={storyboardHighRes || undefined}
                              />
                            ) : (
                              <div className="scene-thumbnail-placeholder">
                                <span className="scene-thumbnail-empty-label">ストーリーボード<br />未生成</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="scenario-card-body-col">
                          {headerBlock}
                          {bodyContent}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={scenario.id}
                      className={`scenario-card ${selectedIds.includes(scenario.id) ? 'scenario-card-selected' : ''}`}
                    >
                      {headerBlock}
                      {bodyContent}
                    </div>
                  );
                })}
            </div>

            {/* ページネーション */}
            <div className="pagination">
              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ← 前へ
                </button>

                {totalPages > 1 && (
                  <div className="pagination-pages">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      // 現在のページ付近のみ表示（省略記号付き）
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            className={`pagination-page ${page === currentPage ? 'active' : ''}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>
                )}

                <button
                  className="pagination-button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  次へ →
                </button>
              </div>

              <div className="pagination-info">
                {totalCount}件中 {startIndex + 1}-{endIndex}件を表示
              </div>
            </div>
          </>
          )}
        </div>

        {/* 一括で開くバー */}
        {selectedIds.length > 0 && (
          <div className="scenario-bulk-bar">
            <span className="scenario-bulk-count">{selectedIds.length}件選択中</span>
            <button
              className="scenario-bulk-clear"
              onClick={() => setSelectedIds([])}
            >
              選択解除
            </button>
            <button
              className="btn-modal-sub scenario-bulk-load"
              onClick={() => {
                if (onSelectScenarios) {
                  onSelectScenarios(selectedIds);
                } else {
                  // fallback: 1件ずつ読み込み
                  selectedIds.forEach(id => onSelectScenario(id));
                }
                setSelectedIds([]);
                onClose();
              }}
            >
              {selectedIds.length}件のシナリオを開く
            </button>
          </div>
        )}
      </div>

      {publishDialog}
    </div>,
    document.body
  );
}
